/**
 * Rule Management API
 *
 * Implements all endpoints for managing compliance rules according to the API contract.
 * Reference: FRONTEND_API_GUIDE.md - Rule Management section
 */

import { apiClient } from "./client";
import type { ApiResponse } from "./client";
import { unwrapPaginatedResponse } from "../utils/apiResponse";
import type { NestedPaginatedResponse } from "../utils/apiResponse";

// ================================
// Type Definitions
// ================================

/**
 * Rule Status Values
 */
export const RuleStatus = {
  DRAFT: "DRAFT",
  PENDING_REVIEW: "PENDING_REVIEW",
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  ARCHIVED: "ARCHIVED",
  SUPERSEDED: "SUPERSEDED",
} as const;

export type RuleStatus = (typeof RuleStatus)[keyof typeof RuleStatus];

/**
 * Priority Values
 */
export const Priority = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL",
} as const;

export type Priority = (typeof Priority)[keyof typeof Priority];

/**
 * Severity Values
 */
export const Severity = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL",
} as const;

export type Severity = (typeof Severity)[keyof typeof Severity];

/**
 * Rule Scope Values
 */
export const RuleScope = {
  GLOBAL: "GLOBAL",
  TENANT: "TENANT",
} as const;

export type RuleScope = (typeof RuleScope)[keyof typeof RuleScope];

/**
 * Rule Category
 */
export interface RuleCategory {
  id: number;
  name: string;
  value: string;
  description?: string;
}

/**
 * Source Document Reference
 */
export interface SourceDocument {
  id: number;
  filename: string;
  mimeType?: string;
  uploadedAt?: string;
}

/**
 * Tagged document linked to a rule (id + display name only)
 */
export interface RuleTaggedDocumentDto {
  id: number;
  name: string;
}

/**
 * Standard paginated response shape used by rule-management endpoints.
 * Note: uses `pageSize` (not `size`) — distinct from BackendPaginatedRuleResponse.
 */
export interface PageResponse<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Query params that control tagged-document embedding on any rule read endpoint.
 */
export interface TaggedDocumentsParams {
  includeTaggedDocuments?: boolean;
  taggedDocumentsPage?: number;
  taggedDocumentsPageSize?: number;
  tagScope?: "direct" | "subtree";
  taggedDocumentsNameContains?: string;
}

/**
 * Rule Set (Document Type) Information
 */
export interface RuleSet {
  id: number;
  name: string;
  displayName: string;
  description: string;
  code: string;
  purpose: string;
  isActive: boolean;
  version?: string | null;
}

export interface ChapterRuleOptionDto {
  chapterRuleId: number;
  ruleNumber: string;
  title: string | null;
  manualReference: string | null;
}

export interface TriggerComplianceEvaluationRequest {
  rulesetId?: number;
  chapterRuleIds?: number[];
}

/**
 * User Reference (for created/updated by)
 */
export interface UserReference {
  id: number;
  name: string;
  email: string;
}

/**
 * Rule Version History Entry
 */
export interface RuleHistoryEntry {
  version: number;
  changes: string;
  changedBy: string;
  changedAt: string;
}

/**
 * Parent/Child Rule Reference
 */
export interface RuleReference {
  id: number;
  ruleNumber: string;
  title: string;
}

/**
 * Backend Rule Response (from API)
 *
 * Note: Backend returns 'title' and 'description' fields directly,
 * not 'ruleName' and 'ruleDescription'
 */
export interface BackendRuleResponse {
  id: number;
  title: string;
  description: string | null;
  code: string | null;
  parentId: number;
  ruleCategoryId: number;
  priority: string | null;
  severity: string | null;
  evaluatorType: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  /**
   * Numeric lifecycle status for the rule (GenericStatus).
   * Optional for backward compatibility with older APIs.
   */
  status?: number | null;
  sourceDocumentId: number;
  createdBy: {
    id: number;
    name: string;
    username: string;
  } | null;
  createdAt: string;
  updatedBy: {
    id: number;
    name: string;
    username: string;
  } | null;
  updatedAt: string;
  evaluatorSpec: Record<string, unknown> | null;
  parameters: Record<string, unknown> | null;
  level?: number;
  root?: boolean;
  leaf?: boolean;

  // Compliance fields (returned by production API)
  ruleText?: string | null;
  ruleDefinition?: string | null;
  pageNumber?: number | null;
  protocolReferences?: string | null;
  requirements?: string | null;
  demonstratingCompliance?: string | null;
  commonGapsOrRisks?: string | null;
  relatedConsiderations?: string | null;
  crossReferences?: string | null;
  example?: string | null;
  manualReference?: string | null;

  // SME fields
  smeRuleEnhancement?: string | null;
  smeNotes?: string | null;

  // Structured objects (returned by production API)
  category?: RuleCategory | null;
  hierarchyPath?: string | null;
  ruleSet?: RuleSet | null;
  parent?: BackendRuleResponse | null;
  children?: BackendRuleResponse[] | null;
  tenantId?: number | null;
  assignedRoleId?: number | null;
  assignedUserId?: number | null;

  // Alternate field names used by some endpoints
  ruleName?: string;
  ruleNumber?: string;
  ruleDescription?: string | null;
  parentRuleId?: number | null;
  isRoot?: boolean;
  isLeaf?: boolean;

  // Tagged document fields (RuleDetailDto)
  taggedDocumentCountDirect?: number | null;
  taggedDocumentCountSubtree?: number | null;
  taggedDocuments?: PageResponse<RuleTaggedDocumentDto> | null;

  evaluableRuleCount?: number | null;
}

/**
 * Complete Rule Model (Frontend)
 */
export interface Rule {
  id: number;
  ruleNumber?: string;
  title: string;
  description: string;
  ruleText: string;
  ruleDefinition?: string | null; // Separate field to track actual ruleDefinition from backend
  ruleCategoryId?: number;
  category?: RuleCategory | null;
  parentRule?: RuleReference | null;
  childRules?: RuleReference[];
  priority?: Priority | string | null;
  severity?: Severity | string | null;
  /**
   * Lifecycle status for the rule.
   *
   * Historically modelled as a string `RuleStatus`; newer APIs use
   * a numeric lifecycle status (see GenericStatus in constants/status.ts).
   * We allow both here for backward compatibility.
   */
  ruleStatus?: RuleStatus | number | string | null;
  scope?: RuleScope;
  effectiveFrom?: string; // ISO date string
  effectiveTo?: string | null; // ISO date string
  sourceDocument?: SourceDocument | null;
  ruleData?: string; // JSON string containing requirements, penalties, etc.
  tenantId?: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy?: UserReference | null;
  updatedBy?: UserReference | null;
  history?: RuleHistoryEntry[];

  // NEW COMPLIANCE FIELDS
  pageNumber?: number | null;
  protocolReferences?: string | null;
  requirements?: string | null;
  demonstratingCompliance?: string | null;
  commonGapsOrRisks?: string | null;
  relatedConsiderations?: string | null;
  crossReferences?: string | null;
  example?: string | null;
  manualReference?: string | null;

  // SME fields
  smeRuleEnhancement?: string | null;
  smeNotes?: string | null;

  /** Number of evaluatable leaf rules under this chapter (ruleCategoryId === 2 only; null otherwise) */
  evaluableRuleCount?: number | null;

  // Backend-specific fields
  level?: number;
  hierarchyPath?: string;
  assignedRoleId?: number | null;
  assignedUserId?: number | null;
  evaluatorType?: string | null;
  isRoot?: boolean;
  isLeaf?: boolean;
  children?: Rule[] | null;
  parent?: Rule | null;
  ruleSet?: RuleSet | null;
  ruleName?: string; // Alias for title
  ruleDescription?: string; // Alias for description

  // Tagged document fields
  taggedDocumentCountDirect?: number | null;
  taggedDocumentCountSubtree?: number | null;
  taggedDocuments?: PageResponse<RuleTaggedDocumentDto> | null;
}

/**
 * Backend Paginated Response
 * Note: Backend uses 'data' field for array of items
 */
export interface BackendPaginatedRuleResponse {
  data: BackendRuleResponse[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first?: boolean;
  last?: boolean;
  numberOfElements?: number;
  hasNext?: boolean;
  hasPrevious?: boolean;
  sort?: {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
    properties: unknown | null;
    orders: unknown | null;
  };
}

/**
 * Paginated Rule Response (Frontend)
 */
export interface PaginatedRuleResponse {
  content: Rule[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  numberOfElements?: number;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

/**
 * Rule Filters for List API
 *
 * Backend API Parameters:
 * - tenantId: Tenant ID filter
 * - categoryName: Category name filter (string)
 * - severity: Severity filter (string)
 * - priority: Priority filter (string)
 * - searchText: Search text
 * - page: Page number (0-based, default: 0)
 * - size: Page size (max 100, default: 20)
 * - sortBy: Sort by field (default: id)
 * - sortDir: Sort direction (asc/desc, default: asc)
 */
export interface RuleFilters {
  tenantId?: number | null; // null = Global, number = specific tenant (admin only)
  categoryName?: string;
  severity?: string;
  priority?: string;
  searchText?: string;
  chapterName?: string; // ALH-267: Filter by chapter (manual_reference)
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: "asc" | "desc";

  /** When true, only returns rules whose subtree tagged-document count is zero */
  onlyRulesWithoutTaggedDocuments?: boolean;

  // Legacy fields (deprecated - will be removed)
  /** @deprecated Use searchText instead */
  search?: string;
  /** @deprecated Use categoryName instead */
  categoryId?: number;
  /** @deprecated Use tenantId instead */
  status?: RuleStatus;
  /** @deprecated No longer supported */
  scope?: RuleScope;
  /** @deprecated No longer supported */
  effectiveFrom?: string;
  /** @deprecated No longer supported */
  effectiveTo?: string;
}

/**
 * Create Rule DTO
 * Matches Swagger API specification: POST /api/rule-management/rules
 */
export interface CreateRuleDto {
  title: string; // Backend expects 'title'
  description: string; // Backend expects 'description'
  parentId?: number | null; // Backend expects 'parentId'
  ruleCategoryId: number; // Backend expects 'ruleCategoryId'
  code?: string; // Optional code field
  evaluatorType?: string; // Optional evaluator type
  effectiveFrom: string; // ISO date string (YYYY-MM-DD format)
  effectiveTo?: string | null; // ISO date string (YYYY-MM-DD format)
  priority: Priority;
  severity: Severity;
  sourceDocumentId?: number | null;
  evaluatorSpec?: Record<string, unknown>; // Optional evaluator specification
  parameters?: Record<string, unknown>; // Optional parameters
  tenantId?: number | null; // null = Global, number = specific tenant (admin only)
  // Legacy fields for backward compatibility (deprecated)
  ruleName?: string; // Deprecated - use 'title' instead
  ruleDescription?: string; // Deprecated - use 'description' instead
  ruleDefinition?: string; // Deprecated
  categoryId?: number; // Deprecated - use 'ruleCategoryId' instead
  parentRuleId?: number | null; // Deprecated - use 'parentId' instead
  scope?: RuleScope; // Deprecated
  ruleData?: string; // Deprecated
  createdBy?: number; // Deprecated

  // NEW COMPLIANCE FIELDS (Optional)
  pageNumber?: number | null;
  protocolReferences?: string | null;
  requirements?: string | null;
  demonstratingCompliance?: string | null;
  commonGapsOrRisks?: string | null;
  relatedConsiderations?: string | null;
  crossReferences?: string | null;
  example?: string | null;
  manualReference?: string | null;
}

/**
 * Update Rule DTO
 * Matches Swagger API specification: PUT /api/rule-management/rules/{id}
 */
export interface UpdateRuleDto {
  title?: string; // Backend expects 'title'
  description?: string; // Backend expects 'description'
  parentId?: number | null; // Backend expects 'parentId'
  ruleCategoryId?: number; // Backend expects 'ruleCategoryId'
  code?: string; // Optional code field
  evaluatorType?: string; // Optional evaluator type
  effectiveFrom?: string; // ISO date string (YYYY-MM-DD format)
  effectiveTo?: string | null; // ISO date string (YYYY-MM-DD format)
  priority?: Priority;
  severity?: Severity;
  sourceDocumentId?: number | null;
  evaluatorSpec?: Record<string, unknown>; // Optional evaluator specification
  parameters?: Record<string, unknown>; // Optional parameters
  tenantId?: number | null; // null = Global, number = specific tenant (admin only)
  // Legacy fields for backward compatibility (deprecated)
  ruleName?: string; // Deprecated - use 'title' instead
  ruleDescription?: string; // Deprecated - use 'description' instead
  ruleDefinition?: string; // Deprecated
  categoryId?: number; // Deprecated - use 'ruleCategoryId' instead
  parentRuleId?: number | null; // Deprecated - use 'parentId' instead
  scope?: RuleScope; // Deprecated
  ruleData?: string; // Deprecated
  updatedBy?: number; // Deprecated

  // NEW COMPLIANCE FIELDS (Optional)
  pageNumber?: number | null;
  protocolReferences?: string | null;
  requirements?: string | null;
  demonstratingCompliance?: string | null;
  commonGapsOrRisks?: string | null;
  relatedConsiderations?: string | null;
  crossReferences?: string | null;
  example?: string | null;
  manualReference?: string | null;

  // SME fields
  smeRuleEnhancement?: string | null;
  smeNotes?: string | null;
}

/**
 * Change Rule Status DTO
 *
 * Matches Swagger API specification for:
 * PATCH /api/rule-management/rules/{id}/status
 *
 * {
 *   "status": 0,
 *   "tenantId": 0
 * }
 */
export interface ChangeRuleStatusDto {
  /**
   * Numeric lifecycle status (see GenericStatus in src/constants/status.ts)
   */
  status: number;
  /**
   * Optional tenant context for multi‑tenant rule updates
   */
  tenantId?: number | null;
}

/**
 * Rule Category List Response
 */
export interface RuleCategoryResponse {
  categories: RuleCategory[];
}

// ================================
// Data Transformation Functions
// ================================

/**
 * Transform Backend Rule to Frontend Rule
 */
function transformBackendRule(backendRule: BackendRuleResponse): Rule {
  // Support both field naming conventions (title/ruleName, description/ruleDescription)
  const title = backendRule.title || backendRule.ruleName || "";
  const description =
    backendRule.description ?? backendRule.ruleDescription ?? "";
  const parentId = backendRule.parentId || backendRule.parentRuleId;

  return {
    id: backendRule.id,
    title,
    description: description || "",
    ruleText: backendRule.ruleText || description || "",
    // ruleDefinition is a separate field from description
    ruleDefinition: backendRule.ruleDefinition ?? null,
    ruleCategoryId: backendRule.ruleCategoryId,
    priority: backendRule.priority || undefined,
    severity: backendRule.severity || undefined,
    // Map numeric lifecycle status (GenericStatus) when provided by backend
    ruleStatus:
      typeof backendRule.status === "number" ? backendRule.status : undefined,
    effectiveFrom: backendRule.effectiveFrom || undefined,
    effectiveTo: backendRule.effectiveTo || undefined,
    createdAt: backendRule.createdAt,
    updatedAt: backendRule.updatedAt,
    createdBy: backendRule.createdBy
      ? {
          id: backendRule.createdBy.id,
          name: backendRule.createdBy.name,
          email: backendRule.createdBy.username,
        }
      : undefined,
    updatedBy: backendRule.updatedBy
      ? {
          id: backendRule.updatedBy.id,
          name: backendRule.updatedBy.name,
          email: backendRule.updatedBy.username,
        }
      : undefined,
    evaluatorType: backendRule.evaluatorType,
    sourceDocument: backendRule.sourceDocumentId
      ? {
          id: backendRule.sourceDocumentId,
          filename: "",
        }
      : undefined,
    parentRule:
      parentId && parentId > 0
        ? { id: parentId, ruleNumber: "", title: "" }
        : undefined,
    // Use the leaf/isLeaf field from backend, default to false to allow lazy loading
    isLeaf: backendRule.leaf ?? backendRule.isLeaf ?? false,
    level: backendRule.level,
    isRoot: backendRule.root ?? backendRule.isRoot,

    // Compliance fields
    pageNumber: backendRule.pageNumber ?? null,
    protocolReferences: backendRule.protocolReferences ?? null,
    requirements: backendRule.requirements ?? null,
    demonstratingCompliance: backendRule.demonstratingCompliance ?? null,
    commonGapsOrRisks: backendRule.commonGapsOrRisks ?? null,
    relatedConsiderations: backendRule.relatedConsiderations ?? null,
    crossReferences: backendRule.crossReferences ?? null,
    example: backendRule.example ?? null,
    manualReference: backendRule.manualReference ?? null,

    // SME fields
    smeRuleEnhancement: backendRule.smeRuleEnhancement ?? null,
    smeNotes: backendRule.smeNotes ?? null,

    // Structured objects
    category: backendRule.category ?? null,
    hierarchyPath: backendRule.hierarchyPath ?? undefined,
    ruleSet: backendRule.ruleSet ?? null,
    parent: backendRule.parent
      ? transformBackendRule(backendRule.parent)
      : null,
    children: backendRule.children?.map(transformBackendRule) ?? null,
    tenantId: backendRule.tenantId ?? null,
    assignedRoleId: backendRule.assignedRoleId ?? null,
    assignedUserId: backendRule.assignedUserId ?? null,

    // Tagged document fields
    taggedDocumentCountDirect: backendRule.taggedDocumentCountDirect ?? null,
    taggedDocumentCountSubtree: backendRule.taggedDocumentCountSubtree ?? null,
    taggedDocuments: backendRule.taggedDocuments ?? null,
    evaluableRuleCount: backendRule.evaluableRuleCount ?? null,

    // Add alias fields for UI compatibility
    ruleName: title,
    ruleNumber: backendRule.ruleNumber || backendRule.ruleName || undefined,
    ruleDescription: description || undefined,
  };
}

/**
 * Transform Backend Paginated Response to Frontend
 */
function transformPaginatedResponse(
  backendResponse: BackendPaginatedRuleResponse,
): PaginatedRuleResponse {
  return {
    content: backendResponse.data.map(transformBackendRule),
    page: backendResponse.page,
    size: backendResponse.size,
    totalElements: backendResponse.totalElements,
    totalPages: backendResponse.totalPages,
    first: backendResponse.first ?? true,
    last: backendResponse.last ?? true,
    numberOfElements:
      backendResponse.numberOfElements ?? backendResponse.data.length,
    hasNext: backendResponse.hasNext ?? false,
    hasPrevious: backendResponse.hasPrevious ?? false,
  };
}

// ================================
// Filter param builder for listRules
// ================================

// Simple direct mappings: filter key → API param name (truthy-check only)
const RULE_FILTER_DIRECT_MAPPINGS: Array<{
  filterKey: keyof RuleFilters;
  paramKey: string;
}> = [
  { filterKey: "categoryName", paramKey: "categoryName" },
  { filterKey: "severity", paramKey: "severity" },
  { filterKey: "priority", paramKey: "priority" },
  { filterKey: "searchText", paramKey: "searchText" },
  { filterKey: "chapterName", paramKey: "chapterName" },
  { filterKey: "sortBy", paramKey: "sortBy" },
];

function buildRuleFilterParams(filters?: RuleFilters): Record<string, unknown> {
  if (!filters) return {};

  const params: Record<string, unknown> = {};

  // tenantId: only include when explicitly a number (not null/undefined)
  if (filters.tenantId !== undefined && filters.tenantId !== null) {
    params.tenantId = filters.tenantId;
  }

  // Apply simple direct mappings (truthy values only)
  for (const { filterKey, paramKey } of RULE_FILTER_DIRECT_MAPPINGS) {
    if (filters[filterKey]) {
      params[paramKey] = filters[filterKey];
    }
  }

  // Pagination — API uses 'pageSize' not 'size'
  if (filters.page !== undefined) params.page = filters.page;
  if (filters.size !== undefined) params.pageSize = filters.size;

  // Sorting — rename sortDir → sortDirection
  if (filters.sortDir) params.sortDirection = filters.sortDir;

  // Boolean filter — must use !== undefined (false is a valid value)
  if (filters.onlyRulesWithoutTaggedDocuments !== undefined) {
    params.onlyRulesWithoutTaggedDocuments =
      filters.onlyRulesWithoutTaggedDocuments;
  }

  // Legacy field support (for backward compatibility)
  if (!filters.searchText && filters.search) {
    params.searchText = filters.search;
  }

  return params;
}

function buildTaggedDocParams(
  p?: TaggedDocumentsParams,
): Record<string, unknown> {
  if (!p) return {};
  const out: Record<string, unknown> = {};
  if (p.includeTaggedDocuments !== undefined)
    out.includeTaggedDocuments = p.includeTaggedDocuments;
  if (p.taggedDocumentsPage !== undefined)
    out.taggedDocumentsPage = p.taggedDocumentsPage;
  if (p.taggedDocumentsPageSize !== undefined)
    out.taggedDocumentsPageSize = p.taggedDocumentsPageSize;
  if (p.tagScope !== undefined) out.tagScope = p.tagScope;
  if (p.taggedDocumentsNameContains)
    out.taggedDocumentsNameContains = p.taggedDocumentsNameContains;
  return out;
}

// ================================
// Rule Management API Class
// ================================

export class RuleManagementApi {
  /**
   * Get All Rule Categories
   *
   * GET /api/rule-management/rule-categories
   *
   * Get all rule categories for use in dropdowns and filters.
   * Returns paginated response with categories array in data.data
   *
   * @returns Paginated response with rule categories
   *
   * @example
   * ```typescript
   * const response = await ruleManagementApi.getCategories();
   * const categories = response?.data?.data || [];
   * ```
   */
  async getCategories(): Promise<
    ApiResponse<{
      data: RuleCategory[];
      page: number;
      pageSize: number;
      totalElements: number;
      totalPages: number;
    }>
  > {
    return await apiClient.get<{
      data: RuleCategory[];
      page: number;
      pageSize: number;
      totalElements: number;
      totalPages: number;
    }>("/rule-management/rule-categories");
  }

  /**
   * Get Chapter List for Dropdown
   *
   * GET /api/rule-management/rules/chapter-list
   *
   * Returns distinct chapter names (manual_reference) for use in filter dropdowns.
   * ALH-267
   *
   * @param tenantId - Optional tenant filter
   * @param rulesetId - Optional ruleset filter
   * @returns Array of chapter name strings
   */
  /**
   * Get Chapter Rules for Compliance Scope Dropdown
   *
   * GET /api/rule-management/rules/chapter-rules
   *
   * Returns chapter-level rules for a given ruleset, used to scope
   * compliance runs and batch evaluation triggers.
   *
   * @param rulesetId - Required ruleset filter
   * @returns Array of ChapterRuleOptionDto
   */
  async getChapterRules(
    rulesetId: number,
  ): Promise<ApiResponse<ChapterRuleOptionDto[]>> {
    return await apiClient.get<ChapterRuleOptionDto[]>(
      "/rule-management/rules/chapter-rules",
      { params: { rulesetId } },
    );
  }

  async getChapterList(
    tenantId?: number | null,
    rulesetId?: number,
  ): Promise<ApiResponse<string[]>> {
    const params: Record<string, unknown> = {};
    if (tenantId !== undefined && tenantId !== null) {
      params.tenantId = tenantId;
    }
    if (rulesetId !== undefined) {
      params.rulesetId = rulesetId;
    }
    return await apiClient.get<string[]>(
      "/rule-management/rules/chapter-list",
      { params },
    );
  }

  /**
   * Get Root Rules
   *
   * GET /api/rule-management/rules/root
   *
   * Get all root-level rules (rules without a parent) for building tree view.
   * This is the starting point for lazy-loading tree structures.
   *
   * @param tenantId - Optional tenant ID (defaults to current user's tenant)
   * @returns Array of root-level rules
   *
   * @example
   * ```typescript
   * const response = await ruleManagementApi.getRootRules();
   * const rootRules = response.data || [];
   * ```
   */
  async getRootRules(
    tenantId?: number,
    taggedDocParams?: TaggedDocumentsParams,
    options?: { onlyRulesWithoutTaggedDocuments?: boolean },
  ): Promise<ApiResponse<Rule[]>> {
    const params: Record<string, unknown> = {};
    if (tenantId !== undefined) params.tenantId = tenantId;
    Object.assign(params, buildTaggedDocParams(taggedDocParams));
    if (options?.onlyRulesWithoutTaggedDocuments !== undefined) {
      params.onlyRulesWithoutTaggedDocuments =
        options.onlyRulesWithoutTaggedDocuments;
    }

    const response = await apiClient.get<{
      data: BackendRuleResponse[];
      page: number;
      pageSize: number;
      totalElements: number;
      totalPages: number;
    }>("/rule-management/rules/root", { params });

    // Use utility to unwrap nested paginated response
    if (response.success && response.data) {
      const { data: rulesData } = unwrapPaginatedResponse({
        success: response.success,
        data: response.data,
      } as NestedPaginatedResponse<BackendRuleResponse>);

      return {
        success: response.success,
        data: rulesData.map(transformBackendRule),
        message: response.message,
        errors: response.errors,
      };
    }

    return {
      success: false,
      data: [],
      message: response.message,
      errors: response.errors,
    };
  }

  /**
   * List Rules (Paginated)
   *
   * GET /api/rule-management/rules
   *
   * Get paginated list of rules including tenant-specific and global rules
   *
   * @param filters - Optional filters (tenantId, categoryName, severity, priority, searchTerm, pagination, sorting)
   * @returns Paginated list of rules
   *
   * @example
   * ```typescript
   * const filters = {
   *   tenantId: 123,
   *   categoryName: 'Safety',
   *   severity: 'HIGH',
   *   searchTerm: 'inspection',
   *   page: 0,
   *   size: 20,
   *   sortBy: 'id',
   *   sortDir: 'asc'
   * };
   * const response = await ruleManagementApi.listRules(filters);
   * ```
   */
  async listRules(
    filters?: RuleFilters,
    taggedDocParams?: TaggedDocumentsParams,
  ): Promise<ApiResponse<PaginatedRuleResponse>> {
    const params = {
      ...buildRuleFilterParams(filters),
      ...buildTaggedDocParams(taggedDocParams),
    };

    // Get response from backend
    const response = await apiClient.get<BackendPaginatedRuleResponse>(
      "/rule-management/rules",
      { params },
    );

    // Transform backend response to frontend format
    if (response.success && response.data) {
      return {
        success: response.success,
        data: transformPaginatedResponse(response.data),
        message: response.message,
        errors: response.errors,
      } as ApiResponse<PaginatedRuleResponse>;
    }

    // Return empty response if no data
    return {
      success: false,
      data: {
        content: [],
        page: 0,
        size: 0,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true,
      },
      message: response.message,
      errors: response.errors,
    };
  }

  /**
   * Get Rule by ID
   *
   * GET /api/rule-management/rules/{id}
   *
   * @param id - Rule ID
   * @returns Full rule details including history, parent/child rules
   */
  async getRuleById(
    id: number,
    taggedDocParams?: TaggedDocumentsParams,
  ): Promise<ApiResponse<Rule>> {
    const params = buildTaggedDocParams(taggedDocParams);
    const response = await apiClient.get<BackendRuleResponse>(
      `/rule-management/rules/${id}`,
      Object.keys(params).length ? { params } : undefined,
    );

    // Transform backend response to frontend format
    if (response.success && response.data) {
      return {
        success: response.success,
        data: transformBackendRule(response.data),
        message: response.message,
        errors: response.errors,
      } as ApiResponse<Rule>;
    }

    // Return error response
    return {
      success: false,
      data: {} as Rule,
      message: response.message,
      errors: response.errors,
    };
  }

  /**
   * Create Rule
   *
   * POST /api/rule-management/rules
   *
   * @param data - Rule creation data
   * @returns Newly created rule
   */
  async createRule(data: CreateRuleDto): Promise<ApiResponse<Rule>> {
    const response = await apiClient.post<BackendRuleResponse>(
      "/rule-management/rules",
      data,
    );

    // Transform backend response to frontend format
    if (response.success && response.data) {
      return {
        success: response.success,
        data: transformBackendRule(response.data),
        message: response.message,
        errors: response.errors,
      } as ApiResponse<Rule>;
    }

    // Return error response
    return {
      success: false,
      data: {} as Rule,
      message: response.message,
      errors: response.errors,
    };
  }

  /**
   * Update Rule
   *
   * PUT /api/rule-management/rules/{id}
   *
   * @param id - Rule ID
   * @param data - Rule update data
   * @returns Updated rule
   */
  async updateRule(
    id: number,
    data: UpdateRuleDto,
  ): Promise<ApiResponse<Rule>> {
    const response = await apiClient.put<BackendRuleResponse>(
      `/rule-management/rules/${id}`,
      data,
    );

    // Transform backend response to frontend format
    if (response.success && response.data) {
      return {
        success: response.success,
        data: transformBackendRule(response.data),
        message: response.message,
        errors: response.errors,
      } as ApiResponse<Rule>;
    }

    // Return error response
    return {
      success: false,
      data: {} as Rule,
      message: response.message,
      errors: response.errors,
    };
  }

  /**
   * Delete Rule (Soft Delete/Archive)
   *
   * DELETE /api/rule-management/rules/{id}
   *
   * @param id - Rule ID
   * @returns Success message
   */
  async deleteRule(id: number): Promise<ApiResponse<{ message: string }>> {
    return await apiClient.delete<{ message: string }>(
      `/rule-management/rules/${id}`,
    );
  }

  /**
   * Change Rule Status
   *
   * PATCH /api/rule-management/rules/{id}/status
   *
   * Transitions rule lifecycle status with validation
   *
   * @param id - Rule ID
   * @param data - Status change payload (numeric status + optional tenant)
   * @returns Updated rule with new status
   */
  async changeRuleStatus(
    id: number,
    data: ChangeRuleStatusDto,
  ): Promise<ApiResponse<Rule>> {
    return await apiClient.patch<Rule>(
      `/rule-management/rules/${id}/status`,
      data,
    );
  }

  /**
   * Get Rule Categories
   *
   * GET /api/rule-management/rules/categories
   *
   * Get all available rule categories (for dropdowns/filters)
   *
   * @returns List of rule categories
   */
  async getRuleCategories(): Promise<ApiResponse<RuleCategoryResponse>> {
    return await apiClient.get<RuleCategoryResponse>(
      "/rule-management/rules/categories",
    );
  }

  /**
   * Get Child Rules
   *
   * GET /api/rule-management/rules/{id}/children
   *
   * Get direct children of a specific rule (for lazy loading tree nodes).
   * Use this to load children on-demand when user expands a tree node.
   *
   * Backend returns paginated response format.
   *
   * @param id - Parent rule ID
   * @param params - Pagination parameters
   * @returns Array of child rules
   *
   * @example
   * const { data } = await ruleManagementApi.getChildRules(100, { page: 1, pageSize: 20 });
   * const children = data || [];
   */
  async getChildRules(
    id: number,
    params?: {
      page?: number;
      pageSize?: number;
      sortBy?: string;
      sortDirection?: string;
      onlyRulesWithoutTaggedDocuments?: boolean;
    } & TaggedDocumentsParams,
  ): Promise<ApiResponse<Rule[]>> {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined)
      queryParams.append("page", params.page.toString());
    if (params?.pageSize !== undefined)
      queryParams.append("pageSize", params.pageSize.toString());
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortDirection)
      queryParams.append("sortDirection", params.sortDirection);
    if (params?.onlyRulesWithoutTaggedDocuments !== undefined) {
      queryParams.append(
        "onlyRulesWithoutTaggedDocuments",
        params.onlyRulesWithoutTaggedDocuments.toString(),
      );
    }

    const taggedDoc = buildTaggedDocParams(params);
    Object.entries(taggedDoc).forEach(([k, v]) =>
      queryParams.append(k, String(v)),
    );

    const queryString = queryParams.toString();
    const url = `/rule-management/rules/${id}/children${queryString ? `?${queryString}` : ""}`;

    const response = await apiClient.get<{
      data: BackendRuleResponse[];
      page: number;
      pageSize: number;
      totalElements: number;
      totalPages: number;
    }>(url);

    // Use utility to unwrap nested paginated response
    if (response.success && response.data) {
      const { data: childRules } = unwrapPaginatedResponse({
        success: response.success,
        data: response.data,
      } as NestedPaginatedResponse<BackendRuleResponse>);

      return {
        success: response.success,
        data: childRules.map(transformBackendRule),
        message: response.message,
        errors: response.errors,
      };
    }

    return {
      success: false,
      data: [],
      message: response.message,
      errors: response.errors,
    };
  }

  /**
   * Get Tagged Documents for a Rule (full pagination)
   *
   * GET /api/rule-management/rules/{id}/documents
   *
   * Use this for a dedicated documents panel / full paging.
   * Prefer embedded `taggedDocuments` on the rule detail for quick first-page previews.
   *
   * @param id - Rule ID
   * @param params - Pagination, name filter and scope
   * @returns Paginated list of tagged documents
   */
  async getTaggedDocuments(
    id: number,
    params?: {
      page?: number;
      pageSize?: number;
      nameContains?: string;
      tagScope?: "direct" | "subtree";
    },
  ): Promise<ApiResponse<PageResponse<RuleTaggedDocumentDto>>> {
    const queryParams: Record<string, unknown> = {};
    if (params?.page !== undefined) queryParams.page = params.page;
    if (params?.pageSize !== undefined) queryParams.pageSize = params.pageSize;
    if (params?.nameContains) queryParams.nameContains = params.nameContains;
    if (params?.tagScope) queryParams.tagScope = params.tagScope;

    return await apiClient.get<PageResponse<RuleTaggedDocumentDto>>(
      `/rule-management/rules/${id}/documents`,
      Object.keys(queryParams).length ? { params: queryParams } : undefined,
    );
  }

  /**
   * Get Rule History
   *
   * GET /api/rule-management/rules/{id}/history
   *
   * Get version history for a rule
   *
   * @param id - Rule ID
   * @returns List of history entries
   */
  async getRuleHistory(id: number): Promise<ApiResponse<RuleHistoryEntry[]>> {
    return await apiClient.get<RuleHistoryEntry[]>(
      `/rule-management/rules/${id}/history`,
    );
  }

  /**
   * Duplicate Rule
   *
   * POST /api/rule-management/rules/{id}/duplicate
   *
   * Create a copy of an existing rule
   *
   * @param id - Rule ID to duplicate
   * @param newTitle - Optional new title for the duplicate
   * @returns Newly created duplicate rule
   */
  async duplicateRule(
    id: number,
    newTitle?: string,
  ): Promise<ApiResponse<Rule>> {
    const body = newTitle ? { title: newTitle } : {};
    return await apiClient.post<Rule>(
      `/rule-management/rules/${id}/duplicate`,
      body,
    );
  }

  /**
   * Search Rules
   *
   * GET /api/rule-management/rules/search
   *
   * Advanced full-text search across rule text, not just title
   *
   * @param query - Search query
   * @param limit - Max results (default: 20)
   * @returns List of matching rules
   */
  async searchRules(
    query: string,
    limit: number = 20,
  ): Promise<ApiResponse<Rule[]>> {
    return await apiClient.get<Rule[]>("/rule-management/rules/search", {
      params: { query, limit },
    });
  }

  /**
   * Validate Rule
   *
   * POST /api/rule-management/rules/validate
   *
   * Validate rule data before creation/update
   *
   * @param data - Rule data to validate
   * @returns Validation result with errors if any
   */
  async validateRule(data: Partial<CreateRuleDto | UpdateRuleDto>): Promise<
    ApiResponse<{
      valid: boolean;
      errors?: Record<string, string[]>;
    }>
  > {
    return await apiClient.post("/rule-management/rules/validate", data);
  }

  /**
   * Export Rules
   *
   * GET /api/rule-management/rules/export
   *
   * Export rules to PDF or Excel format
   *
   * @param filters - Optional filters to apply before export
   * @param format - Export format ('pdf' or 'excel')
   * @returns Blob containing the exported file
   */
  async exportRules(
    filters?: RuleFilters,
    format: "pdf" | "excel" = "pdf",
  ): Promise<Blob> {
    const params: Record<string, unknown> = { format };

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params[key] = value;
        }
      });
    }

    const response = await apiClient.get<Blob>(
      "/rule-management/rules/export",
      {
        params,
        responseType: "blob",
      },
    );

    return response.data;
  }

  /**
   * Bulk Update Rule Status
   *
   * PUT /api/rule-management/rules/bulk/status
   *
   * Update status for multiple rules at once
   *
   * @param ruleIds - Array of rule IDs
   * @param newStatus - New status to apply
   * @param updatedBy - User ID performing the update
   * @returns Success message with count
   */
  async bulkUpdateStatus(
    ruleIds: number[],
    newStatus: RuleStatus,
    updatedBy: number,
  ): Promise<ApiResponse<{ message: string; updatedCount: number }>> {
    return await apiClient.put("/rule-management/rules/bulk/status", {
      ruleIds,
      newStatus,
      updatedBy,
    });
  }

  /**
   * Bulk Delete Rules
   *
   * DELETE /api/rule-management/rules/bulk
   *
   * Delete multiple rules at once
   *
   * @param ruleIds - Array of rule IDs to delete
   * @returns Success message with count
   */
  async bulkDeleteRules(ruleIds: number[]): Promise<
    ApiResponse<{
      message: string;
      deletedCount: number;
    }>
  > {
    return await apiClient.delete("/rule-management/rules/bulk", {
      body: JSON.stringify({ ruleIds }),
    });
  }

  /**
   * Get Rule Statistics
   *
   * GET /api/rule-management/rules/statistics
   *
   * Get statistics about rules (count by status, priority, etc.)
   *
   * @returns Rule statistics
   */
  async getRuleStatistics(): Promise<
    ApiResponse<{
      totalRules: number;
      byStatus: Record<RuleStatus, number>;
      byPriority: Record<Priority, number>;
      bySeverity: Record<Severity, number>;
      byCategory: Record<string, number>;
    }>
  > {
    return await apiClient.get("/rule-management/rules/statistics");
  }

  // ================================
  // Tree Hierarchy Methods
  // ================================

  /**
   * Get Rule Tree (Full Hierarchy)
   *
   * GET /api/rule-management/rules/tree
   *
   * Get the complete rule hierarchy tree with all children loaded recursively.
   * ⚠️ WARNING: Use this only for small rule sets (<500 rules).
   * For large datasets, use getRootRules() + getChildRules() for lazy loading.
   *
   * @param tenantId - Optional tenant ID (defaults to current user's tenant)
   * @returns Array of root rules with children recursively loaded
   *
   * @example
   * const { data } = await ruleManagementApi.getRuleTree();
   * const fullTree = data?.data || [];
   * // Each rule has a 'children' property with nested rules
   */
  async getRuleTree(tenantId?: number): Promise<ApiResponse<Rule[]>> {
    const params = tenantId ? { tenantId } : undefined;
    const response = await apiClient.get<BackendRuleResponse[]>(
      "/rule-management/rules/tree",
      { params },
    );

    // Transform backend response to frontend format
    if (response.success && response.data) {
      return {
        ...response,
        data: response.data.map(transformBackendRule),
      };
    }
    return response as unknown as ApiResponse<Rule[]>;
  }
}

// ================================
// Export Singleton Instance
// ================================

export const ruleManagementApi = new RuleManagementApi();

// Default export
export default ruleManagementApi;
